import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { ChatBot } from "@/components/ChatBot";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Building2, 
  FileText, 
  Globe, 
  Share2, 
  GraduationCap,
  ArrowRight,
  CheckCircle,
  DollarSign,
  MapPin,
  Zap,
  Users
} from "lucide-react";
import { Link } from "react-router-dom";
import heroImage from "@/assets/hero-image.jpg";
import founder1 from "@/assets/founder-1.jpg";
import founder2 from "@/assets/founder-2.jpg";

const Index = () => {
  const coreServices = [
    "Business registration (PTY, Cooperative, Tax, BBBEE)",
    "Compliance & annual returns (CIPC & SARS)",
    "Business plans, profiles & funding proposals",
    "Website design & e-commerce development",
    "Digital form transformation",
    "Social media management",
    "Graphic design & branding",
    "PayPal setup & withdrawal support",
    "Business support & advisory",
  ];

  const whyUs = [
    {
      icon: DollarSign,
      title: "Affordable",
      description: "We design our pricing for real entrepreneurs — not big corporates.",
    },
    {
      icon: MapPin,
      title: "Rural-friendly",
      description: "We work with clients from townships, small towns & villages across SA.",
    },
    {
      icon: Zap,
      title: "Digital + Business expertise",
      description: "We don't just design — we help you grow, comply & structure your business.",
    },
    {
      icon: Users,
      title: "Personal support",
      description: "You work directly with André & Lillian, the founders.",
    },
  ];

  const popularServices = [
    {
      icon: Building2,
      title: "Business Registration & Compliance",
      description: "Start your business correctly with CIPC, SARS, BBBEE & more.",
    },
    {
      icon: Globe,
      title: "Website & E-Commerce Design",
      description: "Grow your online presence with clean, affordable design.",
    },
    {
      icon: Share2,
      title: "Social Media Management",
      description: "Let us handle posting, planning & engagement.",
    },
    {
      icon: FileText,
      title: "Business Documents",
      description: "Professional business plans, profiles & funding proposals.",
    },
    {
      icon: GraduationCap,
      title: "StartRight Business Program",
      description: "Our signature 4-month startup success program.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1">
      {/* Hero Section */}
      <section className="relative bg-gradient-hero py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 space-y-2">
            <p className="text-sm font-semibold text-primary uppercase tracking-wider">
              Catalyst Consulting & Design
            </p>
            <p className="text-lg text-muted-foreground">
              Empowering Small & Rural Businesses Across South Africa
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-in fade-in slide-in-from-left duration-700">
              <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight">
                Build. Grow.{" "}
                <span className="bg-gradient-warm bg-clip-text text-transparent">
                  Thrive.
                </span>
              </h1>
              <p className="text-lg text-muted-foreground">
                Affordable business support, digital solutions & branding designed to help small businesses succeed — no matter where you're starting from.
              </p>
              <div className="space-y-3">
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                    <span>Business registration & compliance</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                    <span>Website design</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                    <span>Business plans & funding proposals</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                    <span>Graphic design & social media management</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                    <span>Digital form transformation</span>
                  </li>
                </ul>
              </div>
              <div className="pt-2">
                <WhatsAppButton size="xl">Tap to Get Started Instantly</WhatsAppButton>
              </div>
            </div>
            <div className="animate-in fade-in slide-in-from-right duration-700 delay-300">
              <img
                src={heroImage}
                alt="Small business owners collaborating"
                className="rounded-2xl shadow-elevated w-full"
                width="1920"
                height="1080"
                fetchPriority="high"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              What We Do
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Catalyst Consulting & Design helps entrepreneurs, startups, and rural businesses build strong foundations, develop professional systems, and grow with confidence.
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <h3 className="text-xl font-semibold text-foreground mb-6 text-center">Our core services:</h3>
            <ul className="grid md:grid-cols-2 gap-4">
              {coreServices.map((service, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-center text-lg text-foreground mt-10 font-medium">
              Everything you need to start, grow, and operate professionally — all in one place.
            </p>
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Why Work With Us?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {whyUs.map((item, index) => (
              <Card 
                key={item.title}
                className="border-border hover:shadow-elevated transition-all duration-300 animate-in fade-in slide-in-from-bottom"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6 space-y-4 text-center">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto">
                    <item.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Popular Services
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {popularServices.map((service, index) => (
              <Card 
                key={service.title} 
                className="group hover:shadow-elevated transition-all duration-300 hover:-translate-y-1 border-border animate-in fade-in slide-in-from-bottom"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6 space-y-4">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-gradient-warm transition-colors">
                    <service.icon className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{service.title}</h3>
                  <p className="text-muted-foreground">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <WhatsAppButton size="lg">Tap WhatsApp to Get a Quote Instantly</WhatsAppButton>
          </div>
        </div>
      </section>

      {/* Meet the Founders Section */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Meet the Founders
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-border hover:shadow-elevated transition-all duration-300">
              <CardContent className="p-8 space-y-6">
                <div className="aspect-square rounded-lg overflow-hidden">
                  <img
                    src={founder1}
                    alt="André Valentine"
                    className="w-full h-full object-cover"
                    width="748"
                    height="749"
                    loading="lazy"
                  />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-foreground">André Valentine</h3>
                  <p className="text-sm font-semibold text-primary">Founder & Lead Consultant</p>
                  <p className="text-muted-foreground">
                    AI specialist, business strategist & digital transformation expert.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border hover:shadow-elevated transition-all duration-300">
              <CardContent className="p-8 space-y-6">
                <div className="aspect-square rounded-lg overflow-hidden">
                  <img
                    src={founder2}
                    alt="Lillian Valentine"
                    className="w-full h-full object-cover"
                    width="1127"
                    height="1126"
                    loading="lazy"
                  />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-foreground">Lillian Valentine</h3>
                  <p className="text-sm font-semibold text-primary">Co-Founder & Operations Lead</p>
                  <p className="text-muted-foreground">
                    Client relations, operations, branding & administrative support.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          <p className="text-center text-muted-foreground max-w-2xl mx-auto mt-8">
            Together, they are committed to uplifting small businesses in South Africa through accessible, high-impact services.
          </p>
        </div>
      </section>

      {/* Catalyst Start Teaser */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto border-primary/20 shadow-elevated overflow-hidden">
            <CardContent className="p-8 md:p-12 text-center space-y-6">
              <span className="inline-block text-sm font-semibold bg-primary/10 text-primary px-4 py-1.5 rounded-full">
                🚀 Coming Soon
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                Catalyst{" "}
                <span className="bg-gradient-warm bg-clip-text text-transparent">Start</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A digital business support platform with AI-powered guidance, professional templates, and step-by-step roadmaps — built for South African entrepreneurs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
                <Link to="/catalyst-start">
                  <Button variant="hero" size="lg">
                    Learn More <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-hero">
        <div className="container mx-auto px-4 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Ready to Grow?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Tap the WhatsApp button below and let's help you build a stronger, more professional business today.
          </p>
          <WhatsAppButton size="xl">Get Started Now</WhatsAppButton>
        </div>
      </section>

      </main>

      <Footer />
      <FloatingWhatsApp />
      <ChatBot />
    </div>
  );
};

export default Index;
